import { useEffect } from "react"

import { formatNumber } from "accounting"
import { Flex, Skeleton, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import InfoTooltip from "../../../Components/InfoTooltip/InfoTooltip"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { selectHomeKpiCards, selectHomeKpiCardsFetching } from "../redux/home.slice"

import styles from "../styles.module.less"

const { Text } = Typography

// The one block behind the Receipt feature flag. The endpoint keeps sending it; a locked merchant
// is shown the placeholder instead of the number, and the card leads nowhere.
const DEFLECT_CARD_KEY = "total_lookups"

// The only thing about a card the backend does not send: where it belongs on this screen. Keyed,
// not positional — a reordered `data_blocks` must not lead a card to another solution.
const CARDS = {
  total_prevented: { icon: "alerts", url: RoutesPaths.dashboard.root },
  [DEFLECT_CARD_KEY]: { icon: "card", url: RoutesPaths.receipts.all },
  chargeback_rate: { icon: "centralhub", url: RoutesPaths.health.overview }
}

// `prefix` + the value at `precision` + `suffix`, exactly as the block spells it, so a card knows
// nothing about percentages or currencies.
const formatBlockValue = (value, { prefix = "", suffix = "", precision = 0 } = {}) =>
  value == null ? null : `${prefix}${formatNumber(value, precision, ",", ".")}${suffix}`

const LockedKpiCard = ({ block }) => {
  const { t } = useTranslation()

  return (
    <div className={cls(styles.kpiCard, styles.kpiCardLocked)} data-testid="home-kpi-locked">
      <Icon type="lock" className={styles.kpiCardIcon} />
      <Flex align="center" gap={8}>
        <Text type="secondary" strong className={styles.kpiCardLabel}>
          {t(block.title)}
        </Text>
      </Flex>
      <span className={styles.lockedBadge}>{t("SOON")}</span>
    </div>
  )
}

const KpiCard = ({ block, comparisonLabel, locked }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const card = CARDS[block.key]
  if (!card) return null
  if (locked) return <LockedKpiCard block={block} />

  const secondary = formatBlockValue(block.secondary_value, {
    prefix: block.secondary_prefix,
    suffix: block.secondary_suffix,
    precision: block.precision
  })
  const comparison = formatBlockValue(block.comparison_value, block)

  return (
    <button
      type="button"
      className={styles.kpiCard}
      onClick={() => navigate(card.url)}
      data-testid={`home-kpi-${block.key}`}
    >
      <Icon type={card.icon} className={styles.kpiCardIcon} />
      <Flex align="center" gap={8}>
        <Text type="secondary" strong className={styles.kpiCardLabel}>
          {t(block.title)}
        </Text>
        {/* Portalled, so hovering it never swallows the card's click. */}
        <InfoTooltip title={t(block.tooltip)} placement="top" />
      </Flex>
      <Flex align="flex-end" justify="space-between" gap={8}>
        <Text className={styles.kpiCardValue}>{formatBlockValue(block.value, block) ?? "—"}</Text>
        {secondary ? (
          <Text type="secondary" className={styles.kpiCardSecondary}>
            {`${secondary} ${t(block.secondary_label || "")}`.trim()}
          </Text>
        ) : null}
      </Flex>
      {comparison ? (
        <Text type="secondary" className={styles.kpiCardCompare}>
          {`${comparison} ${comparisonLabel || t("previous period")}`}
        </Text>
      ) : null}
    </button>
  )
}

// How many placeholders the row holds before the answer names its blocks. The endpoint sends three
// today; being wrong only costs a reflow on arrival, which is why the count is not read from CARDS.
const SKELETON_COUNT = 3

// One request, one row: the page renders the blocks it is given, in the order they arrive.
const KpiCards = ({ getCards, isDeflectLocked }) => {
  const cards = useSelector(selectHomeKpiCards)
  const fetching = useSelector(selectHomeKpiCardsFetching)

  useEffect(() => {
    getCards()
  }, [getCards])

  const blocks = cards?.data_blocks ?? []
  // Only the first load has nothing to show. A refetch keeps the numbers that are already up rather
  // than replacing a readable row with placeholders.
  const isFirstLoad = fetching && blocks.length === 0

  return (
    <div className={styles.kpiRow}>
      {isFirstLoad
        ? Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <Skeleton.Node
              key={index}
              active
              className={styles.kpiCardSkeleton}
              style={{ width: "100%", height: 122, borderRadius: 16 }}
            />
          ))
        : blocks.map((block) => (
            <KpiCard
              key={block.key}
              block={block}
              comparisonLabel={cards?.comparison_period?.label}
              locked={isDeflectLocked && block.key === DEFLECT_CARD_KEY}
            />
          ))}
    </div>
  )
}

export default KpiCards
